import type { OnInit } from '@angular/core'
import { Component, Input, computed, inject, signal } from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'

import { ArticlesService } from '../../../api/articles-service/articles.service'
import { AuthorsService } from '../../../api/authors-service/authors.service'
import type {
  ArticlesDto,
  AuthorDto,
  MagazineDto,
} from '../../../api/responses'
import { ArticleList } from '../../../components/article-list/article-list'
import { BreadcrumbsComponent } from '../../../components/breadcrumbs/breadcrumbs.component'
import { OurSubscriptionsComponent } from '../../../pages/home/components/our-subscriptions/our-subscriptions.component'
import { FormatAuthorNamePipe } from '../../../shared/pipes/format-author-name.pipe'
import type {
  ArticleWithNullableRefs,
  BreadcrumbItem,
  TranslationParams,
} from '../../../types/types'
import { MainInfoComponent } from './components/main-info/main-info.component'

@Component({
  selector: 'dhp-author-details',
  templateUrl: './author-details.component.html',
  styleUrls: ['./author-details.component.scss'],
  providers: [FormatAuthorNamePipe],
  imports: [
    BreadcrumbsComponent,
    MainInfoComponent,
    InfiniteScrollDirective,
    ArticleList,
    OurSubscriptionsComponent,
    TranslatePipe,
  ],
})
export class AuthorDetailsComponent implements OnInit {
  // Inputs for reusability
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() personId!: number
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() personType!: 'author' | 'editor'

  // Signals for state management
  person = signal<AuthorDto | null>(null)
  articles = signal<ArticlesDto[]>([])
  totalArticles = signal<number>(0)
  isLoadingMore = signal<boolean>(false)
  hasMoreItems = signal<boolean>(true)

  error = signal<string | null>(null)
  breadcrumbItems = signal<BreadcrumbItem[]>([])

  // This is really ugly, but to be solved later
  adaptedArticles = computed(() =>
    this.articles().map(article => {
      const mappedArticle: ArticleWithNullableRefs = {
        ...article,
        magazine: article.magazine
          ? ({
              ...article.magazine,
              title: article.magazine.title ?? undefined,
            } as MagazineDto & { title: string | null })
          : null,
        edition: article.edition
          ? {
              ...article.edition,
              edition_version: article.edition.edition_version ?? null,
            }
          : null,
        users: {
          authors: article?.users?.authors?.map(author => ({
            ...author,
            linkType: author.type ?? 'author',
            id: author.id || 0, // Ensure id is always a number
          })),
        },
      }
      return mappedArticle
    })
  )

  // Pagination settings
  private readonly itemsPerPage = 20
  private currentPage = signal<number>(1)

  // Inject services
  private readonly authorsService = inject(AuthorsService)
  private readonly articlesService = inject(ArticlesService)
  private readonly formatAuthorNamePipe = inject(FormatAuthorNamePipe)
  private readonly route = inject(ActivatedRoute)

  ngOnInit(): void {
    this.personId = Number(this.route.snapshot.paramMap.get('id'))
    this.personType = this.route.snapshot.data['type'] as 'author' | 'editor'

    if (!this.personId || isNaN(this.personId)) {
      this.setError('pages.authors.errors.invalidId')
      return
    }

    this.fetchAuthor()
  }

  onScroll(): void {
    this.fetchArticles()
  }

  private fetchAuthor(): void {
    this.authorsService.getAuthorById(this.personId).subscribe({
      next: author => {
        if (!author) {
          this.setError('pages.authors.errors.notFound', { type: 'Author' })
          return
        }

        this.person.set(author)
        this.updateBreadcrumbs()
        this.fetchArticles()
      },
      error: () => {
        this.setError('pages.authors.errors.failedToLoad')
      },
    })
  }

  private fetchArticles(): void {
    if (this.isLoadingMore() || !this.hasMoreItems()) return

    this.isLoadingMore.set(true)

    this.articlesService
      .getArticlesByAuthorId(
        this.personId,
        this.currentPage(),
        this.itemsPerPage
      )
      .subscribe({
        next: ({ articles: newArticles, total }) => {
          this.totalArticles.set(total)
          this.articles.set([...this.articles(), ...newArticles])

          if (newArticles.length < this.itemsPerPage) {
            this.hasMoreItems.set(false)
          }

          this.currentPage.set(this.currentPage() + 1)
          this.isLoadingMore.set(false)
        },
        error: () => {
          this.setError('pages.authors.errors.failedToLoad')
          this.isLoadingMore.set(false)
        },
      })
  }

  private updateBreadcrumbs(): void {
    const person = this.person()
    if (!person) return

    const baseRoute = this.personType === 'author' ? '/authors' : '/editors'

    this.breadcrumbItems.set([
      {
        label:
          this.personType === 'author'
            ? 'pages.authors.authors'
            : 'pages.editors.editors',
        url: baseRoute,
      },
      {
        label: this.formatAuthorNamePipe.transform(person),
        url: `${baseRoute}/${person.id}`,
      },
    ])
  }

  private setError(message: string, _params?: TranslationParams): void {
    this.error.set(message)
  }
}
